import * as React from "react";
import useKBConnection from "../hooks/useKBConnection";
import UploadButton from "./UploadButton";
import useApi from "@/hooks/useApi";
import LoadingSpinner from "@/components/LoadingSpinner";
import useTeams from "@/hooks/useTeams";
import ColorPicker from "@/components/ColorPicker";
import {
  PrimaryBlueButton
} from "@velaro/velaro-component-lib";
import useToastAlert from "@/hooks/useToastAlert";
import { Severity } from "@velaro/velaro-component-lib";

export default function Index() {
  const { connected } = useKBConnection();
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [companyName, setCompanyName] = React.useState("");
  const [themeColor, setThemeColor] = React.useState("");
  const [headerText, setHeaderText] = React.useState("");
  const [returnLinkText, setReturnLinkText] = React.useState("");
  const [returnLinkUrl, setReturnLinkUrl] = React.useState("");
  const [logoUrl, setLogoUrl] = React.useState("");
  const [enableLiveChat, setEnableLiveChat] = React.useState(false);
  const [liveChatGroupId, setLiveChatGroupId] = React.useState<number>(0);
  const [domain, setDomain] = React.useState("");
  const [showHelp, setShowHelp] = React.useState(false);
  const [urlCopied, setUrlCopied] = React.useState(false);
  const [searchEnabled, setSearchEnabled] = React.useState<boolean | null>(null);
  const [reindexing, setReindexing] = React.useState(false);
  const [reindexResult, setReindexResult] = React.useState<{ count: number; message: string } | null>(null);
  const [enableKbBotIndexing, setEnableKbBotIndexing] = React.useState(true);
  const [kbAutoReindexIntervalHours, setKbAutoReindexIntervalHours] = React.useState(24);
  const [hasAI, setHasAI] = React.useState(false);
  // Health stats
  const [lastReindexAt, setLastReindexAt] = React.useState<string | null>(null);
  const [lastReindexCount, setLastReindexCount] = React.useState(0);
  const [staleCount, setStaleCount] = React.useState(0);
  const [totalPublished, setTotalPublished] = React.useState(0);
  // Search preview
  const [previewQuery, setPreviewQuery] = React.useState("");
  const [previewResults, setPreviewResults] = React.useState<Array<{
    title: string; description: string; url: string; topicName: string;
    enableBotResponse: boolean; score: number;
  }>>([]);
  const [previewing, setPreviewing] = React.useState(false);
  const [showPreview, setShowPreview] = React.useState(false);
  const { teams } = useTeams();
  const api = useApi();
  const toastAlert = useToastAlert();

  React.useEffect(() => {
    async function fetchTenant() {
      setLoading(true);
      try {
        const response = await api.admin.get("api/kb/settings");
        const json = await response.json();
        setCompanyName(json?.CompanyName ?? "");
        setThemeColor(json?.ThemeColor ?? "");
        setHeaderText(json?.HeaderText ?? "");
        setEnableLiveChat(json?.EnableLiveChat ?? false);
        setLiveChatGroupId(json?.LiveChatGroupId ?? 0);
        setReturnLinkText(json?.ReturnLinkText ?? "");
        setReturnLinkUrl(json?.ReturnLinkUrl ?? "");
        setLogoUrl(json?.LogoUrl ?? "");
        setDomain(json?.Domain ?? "");
        setEnableKbBotIndexing(json?.EnableKbBotIndexing ?? true);
        setKbAutoReindexIntervalHours(json?.KbAutoReindexIntervalHours ?? 24);
        setHasAI(json?.HasAI ?? false);
      } catch (err) {
        console.error("Failed to fetch KB settings:", err);
      } finally {
        setLoading(false);
      }
    }

    fetchTenant();

    // Check Azure Search status + reindex health
    api.admin.get("api/kb/search/status").then(async (r) => {
      if (r.ok) {
        const data = await r.json();
        setSearchEnabled(data?.enabled ?? false);
        setLastReindexAt(data?.lastReindexAt ?? null);
        setLastReindexCount(data?.lastReindexCount ?? 0);
        setStaleCount(data?.staleCount ?? 0);
        setTotalPublished(data?.totalPublished ?? 0);
      }
    }).catch(() => setSearchEnabled(false));
  }, []);

  async function handleSubmit() {
    setSaving(true);
    try {
      const resp = await api.admin.post("api/kb/settings", {
        companyName,
        headerText,
        themeColor,
        enableLiveChat,
        liveChatGroupId,
        returnLinkText,
        returnLinkUrl,
        logoUrl,
        enableKbBotIndexing
      });

      if (resp.status === 400) {
        toastAlert.displayToast(Severity.Error, "Failed to save settings");
      } else {
        toastAlert.displayToast(Severity.Success, "Settings saved");
      }
    } catch (err) {
      console.error("Failed to save KB settings:", err);
      toastAlert.displayToast(Severity.Error, "Failed to save settings");
    } finally {
      setSaving(false);
    }
  }

  async function triggerReindex() {
    setReindexing(true);
    setReindexResult(null);
    try {
      const resp = await api.admin.post("api/kb/search/reindex", {});
      const data = await resp.json();
      if (data?.success) {
        setReindexResult({ count: data.count, message: data.message });
        toastAlert.displayToast(Severity.Success, `Reindex complete — ${data.count} articles indexed`);
        // Refresh health stats after successful reindex
        api.admin.get("api/kb/search/status").then(async (r) => {
          if (r.ok) {
            const s = await r.json();
            setLastReindexAt(s?.lastReindexAt ?? null);
            setLastReindexCount(s?.lastReindexCount ?? 0);
            setStaleCount(s?.staleCount ?? 0);
            setTotalPublished(s?.totalPublished ?? 0);
          }
        }).catch(() => {});
      } else {
        toastAlert.displayToast(Severity.Error, data?.message ?? "Reindex failed");
      }
    } catch (err) {
      console.error("Reindex error:", err);
      toastAlert.displayToast(Severity.Error, "Reindex failed");
    } finally {
      setReindexing(false);
    }
  }

  async function runPreview(e?: React.FormEvent) {
    e?.preventDefault();
    if (!previewQuery.trim()) return;
    setPreviewing(true);
    setPreviewResults([]);
    try {
      const r = await api.admin.get(`api/kb/search/preview?q=${encodeURIComponent(previewQuery)}`);
      const data = await r.json();
      setPreviewResults(Array.isArray(data?.results) ? data.results : []);
    } catch {
      toastAlert.displayToast(Severity.Error, "Search preview failed");
    } finally {
      setPreviewing(false);
    }
  }

  function formatRelativeTime(isoDate: string | null): string {
    if (!isoDate) return "never";
    try {
      const d = new Date(isoDate);
      const diffMs = Date.now() - d.getTime();
      const mins = Math.floor(diffMs / 60000);
      const hours = Math.floor(diffMs / 3600000);
      const days = Math.floor(diffMs / 86400000);
      if (mins < 2) return "just now";
      if (mins < 60) return `${mins}m ago`;
      if (hours < 24) return `${hours}h ago`;
      return `${days}d ago`;
    } catch { return "unknown"; }
  }

  async function uploadFile(file: File) {
    try {
      const formData = new FormData();
      formData.append("logo", file);

      const response = await api.admin.postFormData("api/kb/settings/upload-logo", formData);
      const json = await response.json();
      setLogoUrl(json?.url ?? "");
      toastAlert.displayToast(Severity.Success, "Logo uploaded");
    } catch (err) {
      console.error("Failed to upload logo:", err);
      toastAlert.displayToast(Severity.Error, "Failed to upload logo");
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-24">
        <LoadingSpinner />
      </div>
    );
  }

  if (connected === null) return null;

  return (
    <div className="p-6 max-w-3xl mx-auto">
      {/* Sticky Save Bar */}
      <div className="sticky top-0 z-20 bg-white border-b border-slate-200 shadow-sm -mx-6 px-6 py-3 mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-lg font-semibold text-slate-800">
            Knowledge Base Settings
          </h1>
          <p className="text-xs text-slate-500">
            Configure how your public knowledge base appears to customers
          </p>
        </div>
        <div className="flex-shrink-0">
          <PrimaryBlueButton
            label={saving ? "Saving..." : "Save Settings"}
            disabled={saving}
            onClick={() => handleSubmit()}
          />
        </div>
      </div>

      {/* Info Dropdown */}
      <div className="mb-6">
        <button
          className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800"
          onClick={() => setShowHelp(!showHelp)}
        >
          <i className={`fa fa-chevron-${showHelp ? "down" : "right"} text-xs`} />
          <i className="fa fa-circle-info" />
          How KB settings work
        </button>
        {showHelp && (
          <div className="mt-3 p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-slate-700 space-y-2">
            <p>
              <strong>Branding</strong> settings control the appearance of your
              public-facing knowledge base site. Customers see the company name,
              logo, theme color, and header text.
            </p>
            <p>
              <strong>Return Link</strong> lets you add a "Back to [your site]"
              link at the top of the KB, so customers can navigate back to your
              main website.
            </p>
            <p>
              <strong>Live Chat</strong> integration adds a chat widget to your
              KB pages. When enabled, customers can start a live chat directly
              from any KB article if they need more help.
            </p>
            <p>
              <strong>Bot integration:</strong> Articles with "Enable as bot response"
              can be surfaced by your AI chatbot. The bot uses the article's bot
              summary and can send a link to the full article.
            </p>
          </div>
        )}
      </div>

      {/* KB Public URL Section */}
      <div className="bg-white rounded-lg border shadow-sm p-6 mb-6">
        <h2 className="text-lg font-medium text-slate-800 mb-4 flex items-center gap-2">
          <i className="fa fa-globe text-blue-500" />
          Your Knowledge Base URL
        </h2>
        {domain ? (
          <div>
            <div className="flex items-center gap-3">
              <a
                href={`https://${domain}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 hover:underline text-sm font-medium"
              >
                https://{domain}
              </a>
              <button
                type="button"
                className="p-1.5 text-slate-400 hover:text-blue-600 rounded hover:bg-blue-50"
                aria-label="Copy URL"
                onClick={() => {
                  navigator.clipboard.writeText(`https://${domain}`);
                  setUrlCopied(true);
                  setTimeout(() => setUrlCopied(false), 2000);
                }}
              >
                <i className={`fa ${urlCopied ? "fa-check text-green-500" : "fa-copy"} text-sm`} />
              </button>
            </div>
            <p className="text-xs text-slate-400 mt-2">
              This is the public URL where your customers can access your
              knowledge base. Share this link or embed it in your website.
            </p>
          </div>
        ) : (
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg overflow-hidden">
            <div className="flex items-start gap-2">
              <i className="fa fa-triangle-exclamation text-amber-500 mt-0.5 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-amber-800">
                  No KB domain configured
                </p>
                <p className="text-xs text-amber-600 mt-1 break-words">
                  Contact your account manager to set up a custom domain for
                  your knowledge base (e.g., help.yourcompany.com). A DNS CNAME
                  record will need to point to our KB hosting service.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        {/* Branding Section */}
        <div className="bg-white rounded-lg border shadow-sm p-6 mb-6">
          <h2 className="text-lg font-medium text-slate-800 mb-4 flex items-center gap-2">
            <i className="fa fa-palette text-blue-500" />
            Branding
          </h2>

          <div className="space-y-4">
            <div>
              <label
                htmlFor="company-name"
                className="block text-sm font-medium text-slate-700 mb-1"
              >
                Company Name
              </label>
              <input
                type="text"
                id="company-name"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Your company name"
                value={companyName}
                onChange={(e) => setCompanyName(e.currentTarget.value)}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Logo
              </label>
              {logoUrl && (
                <div className="flex items-center gap-3 mb-2 p-3 bg-slate-50 rounded-lg">
                  <img
                    src={logoUrl}
                    alt="KB Logo"
                    className="h-10 object-contain"
                  />
                  <button
                    type="button"
                    className="p-1.5 text-red-500 hover:text-red-700 rounded hover:bg-red-50"
                    onClick={() => setLogoUrl("")}
                    aria-label="Remove logo"
                  >
                    <i className="fa fa-trash text-sm" />
                  </button>
                </div>
              )}
              <UploadButton onChange={(file) => uploadFile(file)} />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Theme Color
              </label>
              <ColorPicker
                value={themeColor}
                onChange={(value) => setThemeColor(value)}
              />
            </div>

            <div>
              <label
                htmlFor="header-text"
                className="block text-sm font-medium text-slate-700 mb-1"
              >
                Header Text
              </label>
              <input
                type="text"
                id="header-text"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., How can we help you?"
                value={headerText}
                onChange={(e) => setHeaderText(e.currentTarget.value)}
              />
              <p className="text-xs text-slate-400 mt-1">
                Displayed at the top of your knowledge base homepage
              </p>
            </div>
          </div>
        </div>

        {/* Navigation Section */}
        <div className="bg-white rounded-lg border shadow-sm p-6 mb-6">
          <h2 className="text-lg font-medium text-slate-800 mb-4 flex items-center gap-2">
            <i className="fa fa-link text-blue-500" />
            Navigation
          </h2>

          <div className="space-y-4">
            <div>
              <label
                htmlFor="return-link-text"
                className="block text-sm font-medium text-slate-700 mb-1"
              >
                Return Link Text
              </label>
              <input
                type="text"
                id="return-link-text"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., Back to Our Website"
                value={returnLinkText}
                onChange={(e) => setReturnLinkText(e.currentTarget.value)}
              />
            </div>

            <div>
              <label
                htmlFor="return-link-url"
                className="block text-sm font-medium text-slate-700 mb-1"
              >
                Return Link URL
              </label>
              <input
                type="url"
                id="return-link-url"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="https://yourwebsite.com"
                value={returnLinkUrl}
                onChange={(e) => setReturnLinkUrl(e.currentTarget.value)}
              />
              <p className="text-xs text-slate-400 mt-1">
                Adds a back link at the top of your KB for customers to return
                to your main site
              </p>
            </div>
          </div>
        </div>

        {/* Live Chat Section */}
        <div className="bg-white rounded-lg border shadow-sm p-6 mb-6">
          <h2 className="text-lg font-medium text-slate-800 mb-4 flex items-center gap-2">
            <i className="fa fa-comments text-blue-500" />
            Live Chat Integration
          </h2>

          <div className="space-y-4">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={enableLiveChat}
                onChange={(e) => setEnableLiveChat(e.currentTarget.checked)}
                className="rounded border-slate-300"
              />
              <div>
                <span className="text-sm font-medium text-slate-700">
                  Enable live chat on KB pages
                </span>
                <p className="text-xs text-slate-400">
                  Show a chat widget on your knowledge base so customers can get
                  help from a live agent
                </p>
              </div>
            </label>

            {enableLiveChat && (
              <div>
                <label
                  htmlFor="live-chat-group"
                  className="block text-sm font-medium text-slate-700 mb-1"
                >
                  Chat Team / Group
                </label>
                <select
                  id="live-chat-group"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={liveChatGroupId}
                  onChange={(e) =>
                    setLiveChatGroupId(parseInt(e.currentTarget.value))
                  }
                >
                  <option value={0}>Select a team</option>
                  {(teams ?? []).map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-slate-400 mt-1">
                  Chats from KB pages will be routed to this team
                </p>
              </div>
            )}
          </div>
        </div>

      </form>

      {/* Azure Search Index Section */}
      <div className="bg-white rounded-lg border shadow-sm p-6 mt-6">
        <h2 className="text-lg font-medium text-slate-800 mb-1 flex items-center gap-2">
          <i className="fa fa-magnifying-glass text-blue-500" />
          AI Search Index
        </h2>
        <p className="text-sm text-slate-500 mb-2">
          Syncs your KB articles to Azure AI Search so bots can retrieve relevant
          content and answer customer questions automatically.
        </p>

        {/* How it works callout */}
        <div className="mb-4 p-4 bg-blue-50 border border-blue-100 rounded-lg text-sm text-slate-700 space-y-2">
          <p className="font-medium text-slate-800">How the search index works</p>
          <ul className="space-y-1.5 list-none">
            <li className="flex items-start gap-2">
              <i className="fa fa-robot text-blue-500 mt-0.5 w-4 shrink-0" />
              <span>
                <strong>Bot answers:</strong> When a visitor asks your chatbot a question,
                the bot searches this index for matching articles and uses the article's
                <strong> Bot Summary</strong> field as its reply. The bot summary is a
                short, plain-language version of the article written specifically for
                bot responses — edit it per article in the Articles tab.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <i className="fa fa-file-lines text-blue-500 mt-0.5 w-4 shrink-0" />
              <span>
                <strong>What gets indexed:</strong> Article title, body content, tags,
                and bot summary. Only <em>published</em> articles marked
                "Enable as bot response" are used for bot replies; all published
                articles are searchable on the public KB site.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <i className="fa fa-bolt text-blue-500 mt-0.5 w-4 shrink-0" />
              <span>
                <strong>Automatic updates:</strong> The index updates automatically
                whenever you publish or update an article. Use <em>Reindex All</em>{" "}
                to do a full resync — useful after bulk edits or first setup.
              </span>
            </li>
          </ul>
        </div>

        {searchEnabled === null && (
          <p className="text-sm text-slate-400">Checking index status…</p>
        )}

        {searchEnabled === false && (
          <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
            <div className="flex items-start gap-2">
              <i className="fa fa-circle-info text-slate-400 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-slate-700">
                  Azure Search not configured
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  To enable search indexing, set{" "}
                  <code className="bg-slate-100 px-1 rounded text-xs">KBSearchEndpoint</code>,{" "}
                  <code className="bg-slate-100 px-1 rounded text-xs">KBSearchApiKey</code>, and{" "}
                  <code className="bg-slate-100 px-1 rounded text-xs">KBSearchIndexName</code>{" "}
                  in the Azure App Service configuration.
                </p>
              </div>
            </div>
          </div>
        )}

        {searchEnabled === true && (
          <div className="space-y-5">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-green-500" />
              <span className="text-sm font-medium text-green-700">
                Azure Search connected
              </span>
              {kbAutoReindexIntervalHours > 0 && (
                <span className="text-xs text-slate-400">
                  · auto-reindex every {kbAutoReindexIntervalHours}h
                </span>
              )}
            </div>

            {/* Reindex Health Panel */}
            <div className="grid grid-cols-3 gap-3">
              <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg text-center">
                <div className="text-lg font-semibold text-slate-800">{totalPublished}</div>
                <div className="text-xs text-slate-500 mt-0.5">Published Articles</div>
              </div>
              <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg text-center">
                <div className="text-lg font-semibold text-slate-800">{lastReindexCount}</div>
                <div className="text-xs text-slate-500 mt-0.5">In Index</div>
              </div>
              <div className={`p-3 border rounded-lg text-center ${
                staleCount > 0
                  ? "bg-amber-50 border-amber-200"
                  : "bg-green-50 border-green-200"
              }`}>
                <div className={`text-lg font-semibold ${staleCount > 0 ? "text-amber-700" : "text-green-700"}`}>
                  {staleCount}
                </div>
                <div className={`text-xs mt-0.5 ${staleCount > 0 ? "text-amber-600" : "text-green-600"}`}>
                  Stale
                </div>
              </div>
            </div>
            <p className="text-xs text-slate-400 -mt-1">
              Last indexed: <span className="font-medium">{formatRelativeTime(lastReindexAt)}</span>
              {lastReindexAt && ` (${new Date(lastReindexAt).toLocaleString()})`}
              {staleCount > 0 && (
                <span className="text-amber-600 ml-1">
                  · {staleCount} article{staleCount !== 1 ? "s" : ""} updated since last reindex
                </span>
              )}
            </p>

            {/* Bot indexing toggle — only visible if AI is enabled on this subscription */}
            {hasAI && (
              <div className="p-4 border border-slate-200 rounded-lg bg-slate-50">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={enableKbBotIndexing}
                    onChange={(e) => setEnableKbBotIndexing(e.currentTarget.checked)}
                    className="mt-0.5 rounded border-slate-300"
                  />
                  <div>
                    <span className="text-sm font-medium text-slate-700">
                      Push articles to bot search index
                    </span>
                    <p className="text-xs text-slate-500 mt-0.5">
                      When checked, articles with "Enable as bot response" are automatically
                      pushed to the Azure AI Search index whenever published or updated.
                      The AI bot uses this index to answer visitor questions.
                      Uncheck to decouple your KB from the bot (public KB search still works).
                    </p>
                  </div>
                </label>
              </div>
            )}

            {!hasAI && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
                <i className="fa fa-robot mr-1" />
                Bot indexing requires an AI plan. The search index is still active for
                public KB search — you can reindex below to refresh it. Upgrade to an
                AI plan to also connect articles to your chatbot.
              </div>
            )}

            <div className="flex items-center gap-3">
              <button
                type="button"
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                disabled={reindexing}
                onClick={triggerReindex}
              >
                {reindexing ? (
                  <>
                    <i className="fa fa-circle-notch fa-spin" />
                    Indexing…
                  </>
                ) : (
                  <>
                    <i className="fa fa-arrow-rotate-right" />
                    Reindex All Articles
                  </>
                )}
              </button>

              {reindexResult && !reindexing && (
                <span className="text-sm text-green-600 flex items-center gap-1">
                  <i className="fa fa-check-circle" />
                  {reindexResult.message}
                </span>
              )}
            </div>

            <p className="text-xs text-slate-400">
              Reindex rebuilds the full index from all published public articles.
              {hasAI
                ? " The AI bot uses only articles with \"Enable as bot response\" turned on — you can control this per article in the Articles list."
                : " To control which articles appear in search, publish or unpublish them individually."}
              {" "}Runs in the background — may take a few seconds for large knowledge bases.
              {kbAutoReindexIntervalHours > 0 && " Auto-reindex runs automatically on the interval above."}
            </p>

            {/* Search Preview Panel */}
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <button
                type="button"
                className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
                onClick={() => setShowPreview(!showPreview)}
              >
                <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
                  <i className="fa fa-magnifying-glass text-slate-500" />
                  Test Search Preview
                  <span className="text-xs font-normal text-slate-500">
                    — type a query to see what the index returns
                  </span>
                </div>
                <i className={`fa fa-chevron-${showPreview ? "up" : "down"} text-slate-400 text-xs`} />
              </button>

              {showPreview && (
                <div className="p-4 space-y-3 bg-white">
                  <form onSubmit={runPreview} className="flex gap-2">
                    <input
                      type="text"
                      value={previewQuery}
                      onChange={(e) => setPreviewQuery(e.target.value)}
                      placeholder='e.g. "how do I reset my password"'
                      className="flex-1 px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                      type="submit"
                      disabled={previewing || !previewQuery.trim()}
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1.5"
                    >
                      {previewing
                        ? <><i className="fa fa-circle-notch fa-spin" />Searching…</>
                        : <><i className="fa fa-search" />Search</>}
                    </button>
                  </form>

                  {previewResults.length > 0 && (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {previewResults.map((r, i) => (
                        <div key={i} className="flex items-start gap-3 p-2.5 rounded-lg border border-slate-100 bg-slate-50 hover:bg-white transition-colors">
                          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 text-blue-700 text-xs font-bold flex items-center justify-center">
                            {r.score}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className="text-sm font-medium text-slate-800 truncate">{r.title || "Untitled"}</span>
                              {r.enableBotResponse && (
                                <span className="px-1.5 py-0.5 text-[10px] font-medium rounded-full bg-purple-100 text-purple-700">Bot</span>
                              )}
                            </div>
                            {r.topicName && (
                              <div className="text-xs text-slate-400 mt-0.5">{r.topicName}</div>
                            )}
                            {r.description && (
                              <div className="text-xs text-slate-500 mt-0.5 line-clamp-1">{r.description}</div>
                            )}
                          </div>
                          {r.url && (
                            <a href={r.url} target="_blank" rel="noopener noreferrer"
                              className="flex-shrink-0 text-slate-400 hover:text-blue-600 p-1"
                              title="Open article">
                              <i className="fa fa-external-link text-xs" />
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {!previewing && previewQuery && previewResults.length === 0 && (
                    <p className="text-sm text-slate-500 text-center py-4">
                      No results — try different keywords or reindex if articles are missing.
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
